<template>
  <div class="pg-order">
    <!-- 섹션 레벨: 제목 -->
    <div class="p-field">
      <label class="p-field__label">Section Title</label>
      <input
        v-model="sectionData.content.sectionTitle"
        type="text"
        class="p-input"
        placeholder="섹션 제목"
      />
    </div>

    <!-- 문항 탭 -->
    <question-tabs
      :count="questions.length"
      v-model="currentIndex"
      @add="addQuestion"
      @remove="removeQuestion"
    />

    <template v-if="current">
      <div class="p-field">
        <label class="p-field__label">Question / 지시문</label>
        <input
          v-model="current.questionText"
          type="text"
          class="p-input"
          placeholder="예: 빈 슬롯에 알맞은 순서로 드래그하세요"
        />
      </div>

      <!-- 미리보기 (정답 순서) -->
      <div class="pg-order__preview">
        <span class="pg-order__preview-label">순서 미리보기</span>
        <div class="pg-order__preview-row">
          <template v-if="current.sequence.length">
            <span
              v-for="(s, i) in current.sequence"
              :key="i"
              class="pg-order__preview-cell"
              :class="{ 'is-fixed': s.fixed, 'is-blank': !s.fixed }"
            >{{ s.fixed ? (s.text || "·") : "□" }}</span>
          </template>
          <span v-else class="pg-order__preview-empty">항목을 추가하세요</span>
        </div>
      </div>

      <!-- 순서 항목 -->
      <div class="pg-order__block">
        <div class="pg-order__block-head">
          순서 항목 (정답 순서대로)
          <span class="pg-order__block-sub">고정=미리 표시 / 빈칸=드래그로 채울 슬롯(이 text가 정답)</span>
        </div>
        <div class="pg-order__list">
          <div
            v-for="(item, index) in current.sequence"
            :key="index"
            class="pg-order__item"
            :class="{ 'is-fixed': item.fixed }"
          >
            <span class="pg-order__idx">{{ index + 1 }}</span>
            <input
              v-model="item.text"
              type="text"
              class="p-input pg-order__item-input"
              placeholder="항목 텍스트"
            />
            <label class="pg-order__toggle">
              <input type="checkbox" v-model="item.fixed" />
              <span>고정</span>
            </label>
            <div class="pg-order__item-actions">
              <button
                type="button"
                class="pg-order__icon-btn"
                title="위로 이동"
                :disabled="index === 0"
                @click.prevent="moveUp(index)"
              >
                <i class="material-icons-round">arrow_upward</i>
              </button>
              <button
                type="button"
                class="pg-order__icon-btn"
                title="아래로 이동"
                :disabled="index === current.sequence.length - 1"
                @click.prevent="moveDown(index)"
              >
                <i class="material-icons-round">arrow_downward</i>
              </button>
              <button
                type="button"
                class="pg-order__icon-btn pg-order__icon-btn--danger"
                title="제거"
                @click.prevent="removeItem(index)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>
        </div>
        <div class="pg-order__actions">
          <ABtn kind="yellow" icon="add" @click.prevent="addItem">항목 추가</ABtn>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import ABtn from "@/components/Pineapple/ABtn.vue";
import QuestionTabs from "@/components/Practice/QuestionTabs.vue";
import { useQuestions } from "@/components/Practice/useQuestions";

const sectionData = defineModel({ type: Object, required: true });

const makeEmpty = () => ({
  questionText: "",
  sequence: [{ text: "", fixed: false }]
});

const { questions, currentIndex, current, addQuestion, removeQuestion } = useQuestions(
  sectionData,
  makeEmpty,
  ["questionText", "sequence"]
);

const addItem = () => {
  current.value.sequence.push({ text: "", fixed: false });
};

const removeItem = (index) => {
  current.value.sequence.splice(index, 1);
};

const moveUp = (index) => {
  if (index === 0) return;
  const seq = current.value.sequence;
  const temp = seq[index];
  seq[index] = seq[index - 1];
  seq[index - 1] = temp;
};

const moveDown = (index) => {
  const seq = current.value.sequence;
  if (index === seq.length - 1) return;
  const temp = seq[index];
  seq[index] = seq[index + 1];
  seq[index + 1] = temp;
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-order {
  display: flex;
  flex-direction: column;
  gap: 16px;

  // ── 미리보기 ──
  &__preview {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    background: $p-bg;
  }

  &__preview-label {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: $p-ink-soft;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  &__preview-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__preview-cell {
    min-width: 40px;
    height: 40px;
    padding: 0 8px;
    display: inline-grid;
    place-items: center;
    border-radius: $p-radius-sm;
    font-size: 16px;
    font-weight: 700;

    &.is-fixed {
      border: 1.5px solid $p-line-hard;
      background: $p-panel-alt;
      color: $p-ink-mid;
    }

    &.is-blank {
      border: 1.5px dashed $p-point-deep;
      background: $p-point-soft;
      color: $p-point-deep;
    }
  }

  &__preview-empty {
    font-size: 13px;
    color: $p-ink-soft;
  }

  // ── 블록 ──
  &__block {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;
  }

  &__block-head {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__block-sub {
    font-size: 11.5px;
    font-weight: 500;
    color: $p-ink-soft;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-bg;

    &.is-fixed {
      background: $p-panel-alt;
    }
  }

  &__idx {
    min-width: 24px;
    height: 24px;
    display: inline-grid;
    place-items: center;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__item-input {
    flex: 1;
    min-width: 0;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover:not(:disabled) { background: $p-panel-alt; color: $p-ink; }

    &:disabled { opacity: 0.3; cursor: not-allowed; }

    &--danger {
      color: $p-err;
      &:hover:not(:disabled) { background: $p-err-soft; }
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}
</style>
